import axios from 'axios';
import React, { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Chart } from 'react-google-charts';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, data: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// The named windows the dashboard offers, matching the backend's PERIOD_DAYS.
const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '15d', label: '15 days' },
  { key: '30d', label: '1 month' },
  { key: '90d', label: '3 months' },
];

const money = (n) => '৳ ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = (n) => Number(n || 0).toLocaleString();

// A stat card. Nothing clever — one big number and a caption, colour-coded so
// the queues that need work (unpaid, out of stock) read at a glance.
function Stat({ label, value, sub, tone }) {
  const colours = {
    default: { bg: '#fff', bar: '#0d6efd' },
    warn: { bg: '#fff8e1', bar: '#f0ad4e' },
    danger: { bg: '#fdecef', bar: '#dc3545' },
    good: { bg: '#eafaf0', bar: '#198754' },
  }[tone || 'default'];
  return (
    <Card style={{ borderLeft: `4px solid ${colours.bar}`, background: colours.bg, height: '100%' }}>
      <Card.Body style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 12, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, marginTop: 4 }}>{value}</div>
        {sub ? <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>{sub}</div> : null}
      </Card.Body>
    </Card>
  );
}

function SellerTable({ title, rows, accent }) {
  return (
    <Card className="mb-3">
      <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
      <Table responsive hover size="sm" className="mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>Product</th>
            <th className="text-end">Units</th>
            <th className="text-end">Value</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={r.slug || i}>
              <td>{i + 1}</td>
              <td>{r.name}</td>
              <td className="text-end"><Badge bg={accent}>{num(r.units)}</Badge></td>
              <td className="text-end">{money(r.revenue)}</td>
            </tr>
          ))}
          {(!rows || rows.length === 0) ? (
            <tr><td colSpan={4} className="text-center text-muted">No sales in this period.</td></tr>
          ) : null}
        </tbody>
      </Table>
    </Card>
  );
}

function StockTable({ title, rows, empty, showReserved }) {
  return (
    <Card className="mb-3">
      <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
      <Table responsive hover size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Product</th>
            <th>Category</th>
            <th className="text-end">Stock</th>
            {showReserved ? <th className="text-end">Reserved</th> : null}
            <th className="text-end">Available</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={r.slug || i}>
              <td>{r.name}</td>
              <td>{r.category}</td>
              <td className="text-end">{num(r.stock)}</td>
              {showReserved ? <td className="text-end">{num(r.reserved)}</td> : null}
              <td className="text-end">
                <Badge bg={r.available <= 0 ? 'danger' : r.available <= 5 ? 'warning' : 'secondary'}>
                  {num(r.available)}
                </Badge>
              </td>
            </tr>
          ))}
          {(!rows || rows.length === 0) ? (
            <tr><td colSpan={showReserved ? 5 : 4} className="text-center text-muted">{empty}</td></tr>
          ) : null}
        </tbody>
      </Table>
    </Card>
  );
}

export default function ProductManagerDashboard() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [period, setPeriod] = useState('7d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [productSlug, setProductSlug] = useState('');
  const [products, setProducts] = useState([]);
  const [{ loading, error, data }, dispatch] = useReducer(reducer, {
    loading: true, error: '', data: null,
  });

  const can = userInfo?.permissions || {};
  const isAdmin = Boolean(userInfo?.isAdmin);
  const allow = (p) => isAdmin || can[p];

  // Product list for the filter dropdown. Cheap, cached for the session.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(api + '/api/products', {
          headers: { authorization: `Bearer ${userInfo.token}` },
        });
        const list = Array.isArray(data) ? data : data?.products || [];
        if (live) setProducts(list.map((p) => ({ slug: p.slug, name: p.name })).filter((p) => p.slug));
      } catch {
        /* the filter simply stays empty */
      }
    })();
    return () => { live = false; };
  }, [userInfo]);

  // The one call that fills the whole dashboard. Re-runs when a filter changes.
  const load = async () => {
    dispatch({ type: 'FETCH_REQUEST' });
    try {
      const params = new URLSearchParams();
      // A custom date range wins over the named period, matching the backend.
      if (from || to) {
        if (from) params.set('from', from);
        if (to) params.set('to', to);
      } else {
        params.set('period', period);
      }
      if (productSlug) params.set('slug', productSlug);
      const { data } = await axios.get(api + `/api/reports/dashboard?${params.toString()}`, {
        headers: { authorization: `Bearer ${userInfo.token}` },
      });
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, from, to, productSlug]);

  const chartData = useMemo(() => {
    const rows = [['Day', 'Orders', 'Units']];
    (data?.trend || []).forEach((t) => rows.push([t.date.slice(5), t.orders, t.units]));
    return rows;
  }, [data]);

  const s = data?.summary;
  const stock = data?.stock?.summary;

  return (
    <Container className="py-3">
      <Helmet><title>Product Manager</title></Helmet>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <h3 className="mb-0">Product Manager</h3>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Reports, products, orders and stock{userInfo?.name ? ` — ${userInfo.name}` : ''}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {allow('canViewReports') && <Link to="/pm/reports/sales"><Button size="sm" variant="outline-success">Sales report</Button></Link>}
          {allow('canViewReports') && <Link to="/pm/reports/orders"><Button size="sm" variant="outline-success">Order report</Button></Link>}
          {allow('canAddProduct') && <Link to="/admin/products"><Button size="sm" variant="outline-primary">Manage products</Button></Link>}
          {allow('canManageOrders') && <Link to="/admin/orders"><Button size="sm" variant="outline-primary">Manage orders</Button></Link>}
          {allow('canManageStock') && <Link to="/admin/stock"><Button size="sm" variant="outline-primary">Manage stock</Button></Link>}
        </div>
      </div>

      {/* FILTERS */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs={12} md="auto">
              <div className="btn-group" role="group">
                {PERIODS.map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={!from && !to && period === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => { setFrom(''); setTo(''); setPeriod(p.key); }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>From</Form.Label>
              <Form.Control size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>To</Form.Label>
              <Form.Control size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="mb-0" style={{ fontSize: 12 }}>Product</Form.Label>
              <Form.Select size="sm" value={productSlug} onChange={(e) => setProductSlug(e.target.value)}>
                <option value="">All products</option>
                {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </Form.Select>
            </Col>
            <Col xs="auto">
              {(from || to || productSlug) && (
                <Button size="sm" variant="link" onClick={() => { setFrom(''); setTo(''); setProductSlug(''); setPeriod('7d'); }}>
                  Clear
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? <LoadingBox /> : error ? <MessageBox variant="danger">{error}</MessageBox> : (
        <>
          {/* ORDER STATS */}
          <Row className="g-2 mb-1">
            <Col xs={6} md={3}><Stat label="Orders" value={num(s?.orders)} sub={`${money(s?.revenue)} total`} /></Col>
            <Col xs={6} md={3}><Stat label="Units sold" value={num(s?.units)} sub={`avg ${money(s?.avgOrderValue)}/order`} /></Col>
            <Col xs={6} md={3}><Stat label="Unpaid" value={num(s?.unpaid)} sub={`${num(s?.paid)} paid`} tone={s?.unpaid ? 'warn' : 'good'} /></Col>
            <Col xs={6} md={3}><Stat label="To deliver" value={num(s?.undelivered)} sub={`${num(s?.delivered)} delivered`} tone={s?.undelivered ? 'warn' : 'good'} /></Col>
          </Row>

          {/* TREND */}
          <Card className="mb-3">
            <Card.Header style={{ fontWeight: 600 }}>Orders over time</Card.Header>
            <Card.Body>
              {chartData.length > 1 ? (
                <Chart
                  chartType="AreaChart"
                  width="100%"
                  height="260px"
                  data={chartData}
                  options={{
                    legend: { position: 'top' },
                    colors: ['#0d6efd', '#20c997'],
                    chartArea: { width: '92%', height: '70%' },
                    hAxis: { slantedText: true, slantedTextAngle: 45 },
                    vAxis: { minValue: 0 },
                  }}
                />
              ) : <div className="text-muted text-center py-4">No orders in this period.</div>}
            </Card.Body>
          </Card>

          {/* STOCK STATS */}
          <Row className="g-2 mb-1">
            <Col xs={6} md={3}><Stat label="Products" value={num(stock?.products)} /></Col>
            <Col xs={6} md={3}><Stat label="Available units" value={num(stock?.totalAvailable)} sub={`${num(stock?.totalReserved)} reserved`} /></Col>
            <Col xs={6} md={3}><Stat label="Low stock" value={num(stock?.lowStockCount)} sub={`≤ ${num(stock?.lowThreshold)} left`} tone={stock?.lowStockCount ? 'warn' : 'good'} /></Col>
            <Col xs={6} md={3}><Stat label="Out of stock" value={num(stock?.outOfStockCount)} tone={stock?.outOfStockCount ? 'danger' : 'good'} /></Col>
          </Row>

          {/* SELLERS */}
          <Row>
            <Col md={6}><SellerTable title="Most selling" rows={data?.topSellers} accent="success" /></Col>
            <Col md={6}><SellerTable title="Lowest selling" rows={data?.lowSellers} accent="secondary" /></Col>
          </Row>

          {/* STOCK LISTS */}
          <Row>
            <Col md={6}><StockTable title="Out of stock" rows={data?.stock?.outOfStock} empty="Nothing is out of stock." showReserved /></Col>
            <Col md={6}><StockTable title={`Low stock (≤ ${num(stock?.lowThreshold)})`} rows={data?.stock?.lowStock} empty="Nothing is low." showReserved /></Col>
          </Row>
          <Row>
            <Col md={12}><StockTable title="Highest stock" rows={data?.stock?.highStock} empty="No products." /></Col>
          </Row>
        </>
      )}
    </Container>
  );
}
